import {
  Table as MUITable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import React, { useEffect } from 'react';
import styled from 'styled-components';

type ColumnConfig<T> = {
  title: string;
  mapper:
    | string
    | ((row: T) => React.ReactElement | React.ReactElement[] | number | string);
  descSortFunc?: (a: T, b: T) => number;
};

type RowIdentifier<T> = string | ((row: T) => string | number);

export type TableColumnConfig<T> = ColumnConfig<T>[];

export type TableConfig<T> = {
  rowIdentifier: RowIdentifier<T>;
  columns: TableColumnConfig<T>;
  onRowClick?: (row: T) => unknown;
};

interface Props<T> {
  data: T[];
  config: TableConfig<T>;
}

type OrderOption = 'asc' | 'desc' | 'none';
type OrderStrategy = { order: OrderOption; orderByIndex: number };

const TableRowPointer = styled(TableRow)`
  ${props => (props.hover ? 'cursor: pointer;' : '')}
`;

const getContent = function <T>(columnConf: ColumnConfig<T>, obj: T): any {
  if (typeof columnConf.mapper === 'string') {
    return obj[columnConf.mapper];
  } else if (!columnConf.mapper) {
    return '';
  } else {
    return columnConf.mapper(obj);
  }
};

const getRowIdentifier = function <T>(
  rowIdentifier: RowIdentifier<T>,
  row: T
): string | number {
  if (typeof rowIdentifier === 'string') {
    return row[rowIdentifier];
  } else if (!rowIdentifier) {
    throw Error('rowIdentifier is required for Table config');
  } else {
    return rowIdentifier(row);
  }
};

const getColumnKey = function <T>(column: ColumnConfig<T>, idx: number) {
  return column.title + idx;
};

const Table = function <T>({ data, config }: Props<T>) {
  const [orderedData, setOrderedData] = React.useState<T[]>(data);
  const [orderStrat, setOrderStrat] = React.useState<OrderStrategy>({
    order: 'none',
    orderByIndex: -1,
  });

  useEffect(() => {
    if (orderStrat.order === 'none') {
      setOrderedData(data);
    } else {
      const comparer = config.columns[orderStrat.orderByIndex].descSortFunc;
      const sorted = [...data].sort((a, b) =>
        orderStrat.order === 'desc' ? comparer(a, b) : -comparer(a, b)
      );
      setOrderedData(sorted);
    }
  }, [data, orderStrat]);

  const createHeadCell = (column: ColumnConfig<T>, index) => {
    const key = getColumnKey(column, index);
    if (column.descSortFunc) {
      const sortHandler = () => {
        // if currently not sorted by this column, start with desc
        if (orderStrat.orderByIndex !== index) {
          setOrderStrat({ order: 'desc', orderByIndex: index });
          return;
        } else {
          const nextOrder: { [key: string]: OrderOption } = {
            none: 'desc',
            desc: 'asc',
            asc: 'none',
          };
          setOrderStrat({
            order: nextOrder[orderStrat.order],
            orderByIndex: index,
          });
        }
      };
      return (
        <TableCell key={key}>
          <TableSortLabel
            active={
              orderStrat.order !== 'none' && orderStrat.orderByIndex === index
            }
            direction={orderStrat.order === 'asc' ? 'asc' : 'desc'}
            onClick={sortHandler}
          >
            {column.title}
          </TableSortLabel>
        </TableCell>
      );
    } else {
      return <TableCell key={key}>{column.title}</TableCell>;
    }
  };

  return (
    <TableContainer sx={{ width: '100%' }}>
      <MUITable>
        <TableHead>
          <TableRow>{config.columns.map(createHeadCell)}</TableRow>
        </TableHead>
        <TableBody>
          {orderedData.map(row => {
            return (
              <TableRowPointer
                hover={!!config.onRowClick}
                key={getRowIdentifier(config.rowIdentifier, row)}
                onClick={
                  config.onRowClick ? () => config.onRowClick(row) : null
                }
              >
                {config.columns.map((column, idx) => (
                  <TableCell key={getColumnKey(column, idx)}>
                    {getContent(column, row)}
                  </TableCell>
                ))}
              </TableRowPointer>
            );
          })}
        </TableBody>
      </MUITable>
    </TableContainer>
  );
};

export default Table;
